import { Editor, Element as SlateElement, Point, Range, Transforms, Path } from 'slate';

const LIST_TYPES_ALL = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

export const isAlignType = (format) => TEXT_ALIGN_TYPES.includes(format);
export const isListType = (format) => LIST_TYPES_ALL.includes(format);
export const isAlignElement = (element) => element && 'align' in element;

export const isMarkActive = (editor, format) => {
  try {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
  } catch {
    return false;
  }
};

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

/** Set a mark to a value (e.g. fontSize, color). Pass null to remove. */
export const setMarkValue = (editor, format, value) => {
  if (value == null || value === '') {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, value);
  }
};

/** Get current value of a value-style mark. */
export const getMarkValue = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks != null && format in marks ? marks[format] : undefined;
};

export const isBlockActive = (editor, format, blockType = 'type') => {
  try {
    const { selection } = editor;
    if (!selection) return false;
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => {
          if (!SlateElement.isElement(n)) return false;
          if (blockType === 'align' && isAlignElement(n)) return n.align === format;
          return n.type === format;
        }
      })
    );
    return !!match;
  } catch {
    return false;
  }
};

export const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format, isAlignType(format) ? 'align' : 'type');
  const isList = isListType(format);

  Transforms.unwrapNodes(editor, {
    match: (n) => SlateElement.isElement(n) && isListType(n.type) && !isAlignType(format),
    split: true
  });

  let newProperties;
  if (isAlignType(format)) {
    newProperties = { align: isActive ? undefined : format };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format
    };
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const isLinkActive = (editor) => {
  try {
    const [link] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && n.type === 'link'
    });
    return !!link;
  } catch {
    return false;
  }
};

export const unwrapLink = (editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && n.type === 'link'
  });
};

export const wrapLink = (editor, url) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);

  const link = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : []
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

export const isSelectionValid = (editor) => {
  const { selection } = editor;
  if (!selection) return false;
  try {
    return (
      Editor.hasPath(editor, selection.anchor.path) && Editor.hasPath(editor, selection.focus.path)
    );
  } catch {
    return false;
  }
};

/**
 * If the cursor is at the start of a list item, outdent it to a paragraph (MS Word-style Backspace).
 * Returns true if outdent was performed (caller should preventDefault).
 */
export const tryOutdentListItem = (editor) => {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return false;
  try {
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) => SlateElement.isElement(n) && n.type === 'list-item'
      })
    );
    if (!match) return false;
    const [, path] = match;
    const start = Editor.start(editor, path);
    if (!Point.equals(selection.anchor, start)) return false;
    Transforms.liftNodes(editor, { at: path });
    Transforms.setNodes(editor, { type: 'paragraph' });
    return true;
  } catch {
    return false;
  }
};

/**
 * True if the selection is inside a list item (for Tab/Shift+Tab handling).
 */
export const isInListItem = (editor) => {
  const { selection } = editor;
  if (!selection) return false;
  try {
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) => SlateElement.isElement(n) && n.type === 'list-item'
      })
    );
    return !!match;
  } catch {
    return false;
  }
};

/**
 * Tab: increase list item depth (visual indent, no structural move).
 * Returns true if we're in a list item (caller should preventDefault so focus stays).
 */
export const tryIndentListItem = (editor) => {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return false;
  try {
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) => SlateElement.isElement(n) && n.type === 'list-item'
      })
    );
    if (!match) return false;
    const [node] = match;
    const depth = (node.depth != null ? node.depth : 0) + 1;
    Transforms.setNodes(editor, { depth }, { at: selection });
    return true;
  } catch {
    return false;
  }
};

/**
 * Shift+Tab: decrease list item depth. Returns true if we're in a list item and did something.
 */
export const tryDedentListItem = (editor) => {
  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return false;
  try {
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: (n) => SlateElement.isElement(n) && n.type === 'list-item'
      })
    );
    if (!match) return false;
    const [node] = match;
    const currentDepth = node.depth != null ? node.depth : 0;
    if (currentDepth <= 0) return true; // in list item, caller should still preventDefault
    Transforms.setNodes(editor, { depth: currentDepth - 1 }, { at: selection });
    return true;
  } catch {
    return false;
  }
};

// Parse text-align from style attribute
const getAlignFromElement = (el) => {
  const style = el.getAttribute('style');
  if (!style) return undefined;
  const m = style.match(/text-align:\s*(left|center|right|justify)/);
  return m ? m[1] : undefined;
};

// Parse font-size and color from style attribute for inline spans
const getInlineStyleMarks = (el) => {
  const style = el.getAttribute('style');
  if (!style) return {};
  const marks = {};
  const fontSizeMatch = style.match(/font-size:\s*([^;]+)/);
  if (fontSizeMatch) marks.fontSize = fontSizeMatch[1].trim();
  const colorMatch = style.match(/color:\s*([^;]+)/);
  if (colorMatch) marks.color = colorMatch[1].trim();
  return marks;
};

// Parse inline DOM content to Slate inline nodes (text leaves + links)
const parseInlineContent = (parentEl) => {
  const result = [];
  const ELEMENT_NODE = 1;
  const TEXT_NODE = 3;
  const walk = (el, marks = {}) => {
    if (el.nodeType === TEXT_NODE) {
      const text = el.textContent || '';
      if (!text) return;

      // Split plain text into variable tokens and normal text, e.g. "Hi {name}" ->
      // [{ text: 'Hi ' }, { type: 'variable', key: 'name' }, ...]
      const parts = text.split(/(\{[a-zA-Z0-9_]+\})/g);
      parts.forEach((part) => {
        if (!part) return;
        const match = part.match(/^\{([a-zA-Z0-9_]+)\}$/);
        if (match) {
          const key = match[1];
          result.push({
            type: 'variable',
            key,
            label: key,
            children: [{ text: '' }]
          });
        } else {
          result.push({ ...marks, text: part });
        }
      });
      return;
    }
    if (el.nodeType !== ELEMENT_NODE) return;
    const tag = el.tagName?.toLowerCase();
    if (tag === 'a') {
      const url = el.getAttribute('href') || '';
      const text = el.textContent || '';
      result.push({ type: 'link', url, children: [{ text }] });
      return;
    }
    const nextMarks = { ...marks };
    if (tag === 'strong') nextMarks.bold = true;
    else if (tag === 'em') nextMarks.italic = true;
    else if (tag === 'u') nextMarks.underline = true;
    else if (tag === 'code') nextMarks.code = true;
    else if (tag === 'span') {
      const styleMarks = getInlineStyleMarks(el);
      if (styleMarks.fontSize) nextMarks.fontSize = styleMarks.fontSize;
      if (styleMarks.color) nextMarks.color = styleMarks.color;
    }
    for (const child of el.childNodes) walk(child, nextMarks);
  };
  if (parentEl) for (const child of parentEl.childNodes) walk(child, {});
  return result.length ? result : [{ text: '' }];
};

// Parse ul/ol and nested lists into a flat list of list-items with depth
const parseListToItems = (listEl, depth = 0) => {
  const items = [];
  if (!listEl || !listEl.childNodes) return items;
  const ELEMENT_NODE = 1;
  for (const child of listEl.childNodes) {
    if (child.nodeType !== ELEMENT_NODE) continue;
    const tag = child.tagName?.toLowerCase();
    if (tag === 'li') {
      const align = getAlignFromElement(child);
      items.push({
        type: 'list-item',
        ...(align && { align }),
        ...(depth > 0 && { depth }),
        children: parseInlineContent(child)
      });
    } else if (tag === 'ul' || tag === 'ol') {
      items.push(...parseListToItems(child, depth + 1));
    }
  }
  return items;
};

/**
 * Convert HTML (as produced by this editor's serializeNodeToHtml) back to Slate value.
 * Use when loading saved HTML from the database. Safe to call in SSR (returns default value).
 */
export const deserializeHtmlToSlate = (html) => {
  if (typeof html !== 'string' || !html.trim()) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
  if (typeof document === 'undefined' || !document.createElement) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  if (!body || !body.childNodes || !body.childNodes.length) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
  const ELEMENT_NODE = 1;
  const blocks = [];
  for (const el of body.childNodes) {
    if (el.nodeType === 3) {
      blocks.push({ type: 'paragraph', children: [{ text: el.textContent }] });
      continue;
    }
    if (el.nodeType !== ELEMENT_NODE) continue;
    const tag = el.tagName?.toLowerCase();
    const align = getAlignFromElement(el);
    const base = align ? { align } : {};
    if (tag === 'p') {
      blocks.push({ type: 'paragraph', ...base, children: parseInlineContent(el) });
    } else if (tag === 'h1') {
      blocks.push({ type: 'heading-one', ...base, children: parseInlineContent(el) });
    } else if (tag === 'h2') {
      blocks.push({ type: 'heading-two', ...base, children: parseInlineContent(el) });
    } else if (tag === 'blockquote') {
      blocks.push({ type: 'block-quote', ...base, children: parseInlineContent(el) });
    } else if (tag === 'ul') {
      const listItems = parseListToItems(el);
      blocks.push({
        type: 'bulleted-list',
        ...base,
        children: listItems.length ? listItems : [{ type: 'list-item', children: [{ text: '' }] }]
      });
    } else if (tag === 'ol') {
      const listItems = parseListToItems(el);
      blocks.push({
        type: 'numbered-list',
        ...base,
        children: listItems.length ? listItems : [{ type: 'list-item', children: [{ text: '' }] }]
      });
    }
  }
  return blocks.length ? blocks : [{ type: 'paragraph', children: [{ text: '' }] }];
};

/**
 * When cursor is inside a link and user presses Space or Enter, exit the link
 * so the new character/line is plain text. Returns true if the event was handled.
 */
export const tryExitLinkOnSpaceOrEnter = (editor, event) => {
  const { key } = event;
  if (key !== ' ' && key !== 'Enter') return false;

  const { selection } = editor;
  if (!selection || !Range.isCollapsed(selection)) return false;

  // Find the link node at the current selection
  const [linkEntry] = Editor.nodes(editor, {
    at: selection,
    match: (n) => SlateElement.isElement(n) && n.type === 'link'
  });
  if (!linkEntry) return false;

  const [, linkPath] = linkEntry;
  const endOfLink = Editor.end(editor, linkPath);

  // Only handle when caret is exactly at the end of the link
  if (!Point.equals(selection.anchor, endOfLink)) return false;

  event.preventDefault();

  if (key === ' ') {
    // Insert a space *after* the link and move caret there
    const afterPath = Path.next(linkPath);
    const spaceNode = { text: ' ' };

    Transforms.insertNodes(editor, spaceNode, { at: afterPath });

    const pointAfterSpace = { path: afterPath, offset: 1 };
    Transforms.select(editor, { anchor: pointAfterSpace, focus: pointAfterSpace });
    return true;
  }

  if (key === 'Enter') {
    // Insert a new paragraph *after* the block containing the link
    const [blockEntry] = Editor.nodes(editor, {
      at: selection,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n)
    });
    if (!blockEntry) return false;

    const [, blockPath] = blockEntry;
    const newBlockPath = Path.next(blockPath);
    const newParagraph = { type: 'paragraph', children: [{ text: '' }] };

    Transforms.insertNodes(editor, newParagraph, { at: newBlockPath });

    const start = Editor.start(editor, newBlockPath);
    Transforms.select(editor, { anchor: start, focus: start });
    return true;
  }

  return false;
};